import { AggregateRoot, UniqueEntityId } from "@kernel/ddd";
import { ParticipantData } from "./ParticipantData";
import { Stream } from "../stream";

export interface ParticipantProps {
  remoteId?: UniqueEntityId;
  data: ParticipantData;
  pinned: boolean;
  stream: Stream | null;
}

export abstract class Participant<
  P extends ParticipantProps = ParticipantProps
> extends AggregateRoot<P> {
  public get displayName(): string {
    return this.props.data.displayName;
  }

  public set displayName(displayName: string) {
    this.props.data.displayName = displayName;
  }

  public get type() {
    return this.props.data.type;
  }

  public get data(): ParticipantData {
    return this.props.data;
  }

  public set data(data: ParticipantData) {
    this.props.data = data;
  }

  public get pinned(): boolean {
    return this.props.pinned;
  }

  public isPinned(): boolean {
    return this.props.pinned;
  }

  public pin(): void {
    this.props.pinned = true;
  }

  public unpin(): void {
    this.props.pinned = false;
  }

  public get stream(): Stream | null {
    return this.props.stream;
  }

  public set stream(stream: Stream | null) {
    this.props.stream = stream;
  }

  public addTrack(track: MediaStreamTrack) {
    if (this.stream === null) return;
    this.stream.addTrack(track);
  }

  public toString(): string {
    return this.props.data.displayName;
  }

  public get remoteId(): UniqueEntityId | undefined {
    return this.props.remoteId;
  }

  public set remoteId(remoteId: UniqueEntityId | undefined) {
    this.props.remoteId = remoteId;
  }

  public hasRemoteId(): boolean {
    return this.remoteId !== undefined;
  }

  public abstract isLocal(): boolean;

  public clone(): Participant<P> {
    return new (this.constructor as new (
      id: UniqueEntityId,
      props: P
    ) => Participant<P>)(new UniqueEntityId(this.id.toValue()), {
      ...this.props
    });
  }
}
